import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import type { ColumnConfiguration, ListBuilderConfiguration } from '../types';

/**
 * Lot column for the invoice line-items card (PD-15838). Shipped addable-but-off:
 * `invoice_detail.lotNumber` is only populated by ERPs that report lot allocations
 * (Blue Link), so it is left out of the default columns and offered through the
 * field picker instead. The Blue Link tenants get it on by default through an
 * instance migration in apps/config-api that inserts this same definition into
 * their saved layouts — import it there rather than restating it, so the two
 * cannot drift.
 */
export const INVOICE_DETAIL_LOT_COLUMN: ColumnConfiguration = {
	id: 'lot',
	headers: [{ id: 'lot-header', text: 'Lot' }],
	fields: [
		{
			id: 'lot-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lotNumber',
					objectTable: 'invoice_detail',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textPrimary' },
			},
			displayStyle: 'text',
		},
	],
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'lotNumber',
			objectTable: 'invoice_detail',
		},
	],
	alignment: 'left',
	minWidth: 120,
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'top',
};

/**
 * Invoice line items list configuration.
 *
 * Used by the Line Items card on the Invoice Details overview page. Mirrors the
 * stacked-column layout of the original hand-coded table so the visual
 * default matches what users see today, while exposing every field through
 * the list configuration builder for per-tenant customization.
 *
 * The query function for this list (in InvoiceLineItemsCard) flattens the
 * nested `item.*` data the API returns — `itemNumber`, `sizeFormatted`,
 * `itemId`, and a numeric `discount` are all pre-computed so the list
 * configuration can reference flat fields.
 */
export const INVOICE_DETAIL_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-invoice-line-items-all',
			cacheTTL: 600000,
			name: 'Invoice Line Items',
			type: 'default',
			active: true,
			sortOrder: 0,
			defaultSortId: 'line-number-asc',
			columns: [
				{
					id: 'line-number',
					// Header intentionally blank — the line number is self-evident,
					// and the column is sized to fit 3 digits plus cell padding.
					headers: [{ id: 'line-number-header', text: '' }],
					fields: [
						{
							id: 'line-number-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'lineNumber',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'theme',
									themeColor: 'textPrimary',
								},
								fontWeight: 'semibold',
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'lineNumber',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'center',
					// The runtime table reads top-level width / minWidth / maxWidth
					// (widthConfig is only consumed by the configurator UI), so pin
					// the actual column width here. Sized for 3 digits + sort caret.
					width: 60,
					minWidth: 60,
					maxWidth: 60,
					widthConfig: { mode: 'fixed', width: 60 },
					verticalAlignment: 'middle',
				},
				{
					// Combined item column: description (linked) on line 1,
					// manufacturer on line 2, item number followed by attribute
					// icons on line 3 — mirrors the layout used by the main item
					// list (libs/core/src/list-builder/configs/item.config.ts).
					id: 'item',
					headers: [{ id: 'item-header', text: 'Item' }],
					fields: [
						{
							id: 'item-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemName',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'link' },
								fontWeight: 'semibold',
							},
							link: {
								enabled: true,
								type: 'object',
								objectTable: 'item',
								idField: 'itemId',
							},
							displayStyle: 'text',
						},
						{
							id: 'item-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'line_break',
						},
						{
							id: 'manufacturer-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'manufacturer',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'item-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'line_break',
						},
						{
							id: 'item-number-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemNumber' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							// Self-contained custom component — pulls
							// isShortDate / isRefrigerated / isHazmat /
							// effectiveDeaSchedule directly from the row.
							// Sits on the same line as itemNumber because there's
							// no line_break between them.
							id: 'item-attribute-icons',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'sm' },
							displayStyle: 'custom',
							customComponent: { id: 'shop-item-attribute-icons' },
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'itemName',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'left',
					minWidth: 250,
					width: 280,
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'size-strength',
					headers: [
						{ id: 'size-header', text: 'Size' },
						{ id: 'strength-header', text: 'Strength' },
					],
					fields: [
						{
							id: 'size-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'sizeFormatted' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'size-strength-break',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'strength-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'strength',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					alignment: 'left',
					minWidth: 120,
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'price',
					headers: [{ id: 'price-header', text: 'Price' }],
					fields: [
						{
							id: 'price-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'price',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'price',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantity',
					headers: [{ id: 'quantity-header', text: 'Quantity' }],
					fields: [
						{
							id: 'quantity-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'quantity',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
								formatOptions: { numberFormat: 'auto' },
							},
							displayStyle: 'number',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'quantity',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'right',
					minWidth: 80,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'subtotal',
					headers: [{ id: 'subtotal-header', text: 'Subtotal' }],
					fields: [
						{
							id: 'subtotal-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'subtotal',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'subtotal',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'right',
					minWidth: 100,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'tax-freight-discount',
					headers: [
						{ id: 'tax-header', text: 'Tax' },
						{ id: 'freight-header', text: 'Freight' },
						{ id: 'discount-header', text: 'Discount' },
					],
					fields: [
						{
							id: 'tax-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'tax',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'tfd-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'freight-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'freight',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'tfd-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'discount-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'discount' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'conditional',
									rules: [
										{
											// The conditional color engine reads
											// `field.fieldName`; a top-level `fieldName`
											// is silently ignored, so the condition
											// would never fire.
											field: { fieldName: 'discount' },
											operator: 'not_equals',
											value: 0,
											color: { themeColor: 'success' },
										},
									],
									defaultColor: { themeColor: 'textSecondary' },
								},
							},
							displayStyle: 'currency',
						},
					],
					sortable: false,
					alignment: 'right',
					minWidth: 120,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'total-gp',
					headers: [
						{ id: 'total-header', text: 'Total' },
						{ id: 'total-gp-header', text: 'Total GP' },
					],
					fields: [
						{
							id: 'total-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'total',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'theme', themeColor: 'textPrimary' },
								fontWeight: 'semibold',
							},
							displayStyle: 'currency',
						},
						{
							id: 'total-break',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'xs' },
							displayStyle: 'line_break',
						},
						{
							id: 'total-gp-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'totalGP',
									objectTable: 'invoice_detail',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
							requiredPermission: GP_VIEW_PERMISSION,
						},
					],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'total',
							objectTable: 'invoice_detail',
						},
					],
					alignment: 'right',
					minWidth: 120,
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
			],
			description: 'Line items for an invoice',
			requiredFields: [
				'invoice_detail._id',
				'invoice_detail._fk_item__id',
				'invoice_detail.lineNumber',
			],
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			// Object link resolver (useListLinkNavigation) finds the entry whose
			// objectTable matches the field's `link.objectTable`; without an
			// 'item' entry here, item-detail links would fall back to this
			// invoice_detail baseUrl and render with a literal `:invoiceId` in
			// the URL because the resolver only substitutes `:id`.
			baseUrl: '/items/:id',
			objectLabel: 'Item',
			objectTable: 'item',
			selectedFields: [],
		},
		{
			baseUrl: '/invoices/:invoiceId/details/:id',
			objectLabel: 'Invoice Line Item',
			objectTable: 'invoice_detail',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'lineNumber', fieldLabel: 'Line Item' },
				{ fieldName: 'itemNumber', fieldLabel: 'Item #' },
				{ fieldName: 'itemName', fieldLabel: 'Description' },
				{ fieldName: 'manufacturer', fieldLabel: 'Manufacturer' },
				{ fieldName: 'sizeFormatted', fieldLabel: 'Size' },
				{ fieldName: 'strength', fieldLabel: 'Strength' },
				{ fieldName: 'lotNumber', fieldLabel: 'Lot' },
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'quantity', fieldLabel: 'Quantity' },
				{ fieldName: 'subtotal', fieldLabel: 'Subtotal' },
				{ fieldName: 'tax', fieldLabel: 'Tax' },
				{ fieldName: 'freight', fieldLabel: 'Freight' },
				{ fieldName: 'discount', fieldLabel: 'Discount' },
				{ fieldName: 'total', fieldLabel: 'Total' },
				{ fieldName: 'totalGP', fieldLabel: 'Total GP' },
			],
		},
	],
	tableSorts: [
		{
			id: 'line-number-asc',
			name: 'Line Number',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lineNumber',
					objectTable: 'invoice_detail',
				},
			],
			description: 'Sort by line number',
		},
		{
			id: 'description-asc',
			name: 'Description',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'itemName',
					objectTable: 'invoice_detail',
				},
			],
			description: 'Sort by item description',
		},
		{
			id: 'total-desc',
			name: 'Total',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'total',
					objectTable: 'invoice_detail',
				},
			],
			description: 'Sort by total (highest first)',
		},
	],
};
